<template>
    <div>
        <!-- HEADER -->
        <v-row align="center">
            <!-- GROUP BY -->
            <v-col cols="12" class="py-0 mt-n3">
                <group-by-students-selector
                    @change="currentGroupBy = $event"
                />
            </v-col>
            <!-- COACH FILTER -->
            <v-col cols="12" v-if="isCompany" class="pb-0">
                <v-row>
                    <v-col cols="6" offset-md="4" offset-lg="3" class="pb-0">
                        <kimsa-combobox
                            :placeholder="$t('inputs.coaches_placeholder')"
                            outlined dense solo
                            :items="coaches"
                            @change="setCurrentCoach"
                            :value="currentCoach"
                            item-value="objectID"
                            clearable searchable
                            icon="$account"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <!-- MONTH SELECTOR -->
            <v-col cols="4" lg="3">
                <month-selector
                    v-if="$refs.calendar"
                    @prev="$refs.calendar.prev()"
                    @next="$refs.calendar.next()"
                    :title="$refs.calendar.title"
                />
            </v-col>
            <!-- STUDENT FILTER -->
            <v-col cols="6" v-if="currentGroupBy === 'students'">
                <kimsa-combobox
                    :placeholder="$t('inputs.students_placeholder')"
                    outlined dense solo
                    :items="students"
                    @change="setCurrentStudent"
                    :value="currentStudent"
                    item-value="objectID"
                    clearable searchable
                    with-list-icon
                    icon="$account"
                />
            </v-col>
            <!-- GROUP FILTER -->
            <v-col cols="6" v-if="currentGroupBy === 'groups'">
                <kimsa-combobox
                    :placeholder="$t('inputs.groups_placeholder')"
                    outlined dense solo
                    :items="groupsWithIcons"
                    @change="setCurrentGroup"
                    :value="currentGroup"
                    item-value="objectID"
                    clearable searchable
                    with-list-icon
                    icon="$account_multiple"
                />
            </v-col>
            <!-- TEAM FILTER -->
            <template v-if="currentGroupBy === 'teams'">
                <v-col cols="3">
                    <kimsa-combobox
                        :placeholder="$t('inputs.teams_placeholder')"
                        outlined dense solo
                        :items="teams"
                        @change="setCurrentTeam"
                        :value="currentTeam"
                        @clear="resetTeam"
                        item-value="objectID"
                        clearable searchable
                        icon="$account_multiple"
                    />
                </v-col>
                <v-col cols="3">
                    <kimsa-combobox
                        :placeholder="$t('inputs.sub_teams_placeholder')"
                        outlined dense solo
                        :items="teamGroups"
                        @change="setCurrentTeamGroup"
                        :readonly="!currentTeam"
                        :value="currentTeamGroup"
                        item-value="objectID"
                        clearable searchable
                        with-list-icon
                        icon="$account_multiple"
                    />
                </v-col>
            </template>
            <!-- DATE FILTER -->
            <v-col cols="2" lg="3">
                <group-by-selector
                    height="33" rounded
                    :items="dateTypesOptions"
                    @change="dateType = $event"
                    :value="dateType"
                />
            </v-col>
            <!-- CURRENT WEEK STATUS -->
            <v-col cols="12" class="py-0" v-if="currentSelection">
                <v-row no-gutters>
                    <kimsa-combobox
                        label="Trabajo por"
                        outlined dense solo
                        :items="weekOptions"
                        class="mr-3"
                        style="max-width: 200px"
                        @change="onChangeWeekOption"
                        :value="currentWeekOption"
                    />
                    <v-radio-group v-model="currentWeekStatus" row hide-details class="mt-6">
                        <v-radio
                            v-for="(week, i) in weekStatus"
                            :key="i"
                            :label="week.name"
                            :value="week.value"
                            :color="week.color"
                        ></v-radio>
                    </v-radio-group>
                </v-row>
            </v-col>
        </v-row>
        <!-- BODY -->
        <v-row align="center">
            <!-- ALERT -->
            <v-col v-if="!isSelectedOption" cols="12">
                <v-alert color="secondary" outlined icon="mdi-alert-circle-outline" prominent border="left">
                    {{ alertMessage }}
                </v-alert>
            </v-col>
            <!-- LOADER -->
            <v-col v-else-if="loadingEvents" cols="12">
                <loading-spinner color="secondary"/>
            </v-col>
            <!-- CALENDAR -->
            <v-col v-show="isSelectedOption && !loadingEvents" cols="12">
                <div class="p-relative">
                    <v-calendar
                        ref="calendar"
                        v-model="focus"
                        color="#99CC00"
                        :events="events"
                        :event-height="30"
                        event-color="transparent"
                        :type="dateType"
                        @change="updateRange"
                        locale="es"
                        :weekdays="weekdays"
                        :weekday-format="getDayString"
                        class="calendar"
                        @mousedown:event="startDrag"
                        @mouseup:event="endDrag"
                        @mousemove:day="mouseMoveUpdate"
                        @mouseup:day="endDragFreeSpace"
                    >
                        <!-- FOR EACH EVENT DATA -->
                        <template v-slot:event="item">
                            <exercise-event
                                :style="getEventStyles(item)"
                                :event="item"
                                @click:show="showEvent(item)"
                                @click:delete="showDeleteEvent(item)"
                            />
                        </template>

                        <!-- FOR MONTH VIEW -->
                        <template v-slot:day-label="{ day, month, date }">
                            <day-label-calendar
                                :day="day"
                                :is-current-month="thisMonth(month)"
                                :has-events-today="hasEventsToday(date)"
                                :note="noteToday(date)"
                                :can-lock-day="currentGroupBy === 'students'"
                                @click:day="viewDay(date)"
                                @click:add="showAddExercise(date)"
                                @click:copy="showCopyRoutine(date)"
                                @click:copy:week="showCopyWeek(date)"
                                @click:lock="showLockDay([date])"
                                @click:save:program="showSaveProgramDialog(date)"
                                @click:save:routine="showSaveRoutineDialog(date)"
                                @click:delete="showDeleteRoutine(date)"
                                @click:note="showNoteDialog(date)"
                            />
                        </template>
                        <template v-slot:day="{ month, date }">
                            <div v-if="thisMonth(month)">
                                <!-- ADD EXERCISE -->
                                <div class="text-right pr-1" v-if="hasEventsToday(date)">
                                    <button-tooltip
                                        @click="showAddExercise(date)"
                                        fab depressed color="#F9F9F9"
                                        height="30" width="30"
                                        :tooltip="$t('tooltips.add_exercise')"
                                    >
                                        <v-icon size="13" color="#078571">$add_circle</v-icon>
                                    </button-tooltip>
                                </div>
                                <!-- NO ROUTINE TODAY -->
                                <no-routines-in-date v-else :dragging="dragging"/>
                            </div>
                        </template>

                        <!-- FOR DAY/WEEK VIEW -->
                        <template v-slot:day-label-header="{ day, month, date }">
                            <day-label-calendar
                                :day="day"
                                :is-current-month="thisMonth(month)"
                                :has-events-today="hasEventsToday(date)"
                                :note="noteToday(date)"
                                :can-lock-day="currentGroupBy === 'students'"
                                @click:day="viewDay(date)"
                                @click:add="showAddExercise(date)"
                                @click:copy="showCopyRoutine(date)"
                                @click:copy:week="showCopyWeek(date)"
                                @click:lock="showLockDay(date)"
                                @click:save:program="showSaveProgramDialog(date)"
                                @click:save:routine="showSaveRoutineDialog(date)"
                                @click:delete="showDeleteRoutine(date)"
                                @click:note="showNoteDialog(date)"
                            />
                        </template>
                        <template v-slot:day-header="{ month, date }">
                            <div v-if="thisMonth(month)">
                                <!-- ADD EXERCISE -->
                                <div class="text-right pr-1" v-if="hasEventsToday(date)">
                                    <button-tooltip
                                        @click="showAddExercise(date)"
                                        fab depressed color="#F9F9F9"
                                        height="30" width="30"
                                        :tooltip="$t('tooltips.add_exercise')"
                                    >
                                        <v-icon size="13" color="#078571">$add_circle</v-icon>
                                    </button-tooltip>
                                </div>
                                <!-- NO ROUTINE TODAY -->
                                <no-routines-in-date v-else :dragging="dragging"/>
                            </div>
                        </template>

                    </v-calendar>

                    <!-- OVERLAY  -->
                    <v-overlay :value="loadingDragging" class="calendar-overlay" opacity="0.3" z-index="1">
                        <v-progress-circular indeterminate color="primary"/>
                    </v-overlay>
                </div>

                <!-- LOCK DAYS -->
                <lock-days-dialog
                    :student="currentStudent"
                    :current-lock-days="lockDays"
                    ref="lockDaysDialog"
                />

                <!-- ADD EXERCISE -->
                <add-exercise-desktop-dialog
                    @success="onAddExercise"
                    :current-selection="currentSelection"
                    :current-routines="currentRoutines"
                    :collection="collectionName"
                    :assigned-routine-to-replace="assignedRoutineToReplace"
                    :coach-id="coachId"
                    ref="addExerciseDialog"/>

                <!-- SAVE ROUTINE -->
                <save-assigned-routine-dialog
                    :coach-id="coachId"
                    :collection="collectionName"
                    ref="saveRoutine"
                />

                <!-- SAVE PROGRAM -->
                <save-program-dialog
                    :coach-id="coachId"
                    :current-selection="currentSelection"
                    ref="saveProgram"
                />

                <!-- COPY ROUTINE -->
                <copy-routine-dialog
                    :coach-id="coachId"
                    :dates="dates"
                    @success="onCopyRoutine"
                    :students-options="students"
                    :groups-options="groups"
                    :teams-options="teams"
                    :current-selection="currentSelection"
                    ref="copy"/>

                <!-- SELECT REPLACE ROUTINES -->
                <select-replace-assigned-routines-dialog
                    :assigned-routines-in-same-date="assignedRoutinesInSameDate"
                    @confirm="onConfirmReplace($event)"
                    ref="replaceRoutines"
                />

                <!-- CONFIRM DELETE -->
                <kimsa-confirm
                    :title="confirmTitle"
                    :content="confirmContent"
                    @confirm="confirmDeleteExercise ? onDeleteExercise() : onDeleteRoutine()"
                    :close-on-confirm="false"
                    :loading="loadingDelete"
                    ref="confirm"/>

                <!-- CONFIRM COPY WEEK -->
                <kimsa-confirm
                    :title="$t('routines.copy_week_title')"
                    :content="$t('routines.copy_week_subtitle')"
                    @confirm="onConfirmCopyWeek()"
                    :close-on-confirm="false"
                    :loading="loadingCopyWeek"
                    ref="copyWeek"/>

                <!-- ADD NOTES TO ROUTINE -->
                <notes-routine-dialog
                    :current-selection="currentSelection"
                    :collection="collectionName"
                    ref="notes"
                    @saved="onSaveNotes"/>

                <!-- MESSAGE FOR DRAGGING -->
                <v-slide-x-reverse-transition>
                    <v-alert v-show="dragging" class="tip-dragging" type="info" color="accent">
                        {{ $t('routines.tip_dragging') }}
                    </v-alert>
                </v-slide-x-reverse-transition>
            </v-col>
        </v-row>
        <!-- BLOCKS FOOTER -->
        <block-footer :colors="blockColors"/>
    </div>
</template>

<script>

import draggable from 'vuedraggable'
import AddExerciseDesktopDialog from "@/components/coach/routines/AddExerciseDesktopDialog";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import {Client, UserIndex} from "@/utils/Algolia";
import {mapActions, mapState} from "vuex";
import CopyRoutineDialog from "@/components/common/routines/CopyRoutineDialog";
import {randomString} from '@/utils/Common'
import {
    addAssignedRoutinesGroup, clearBlocksAssignedRoutine,
    deleteAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getMergedBlocks, getPayloadBaseAssignedRoutine, getPayloadExercise,
    hasAssignedRoutineInDate,
    updateAssignedRoutinesGroup
} from "@/utils/Exercises";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import {db} from "@/utils/Firebase";
import SelectReplaceAssignedRoutinesDialog from "@/components/coach/routines/SelectReplaceAssignedRoutinesDialog";
import SaveAssignedRoutineDialog from "@/components/coach/routines/SaveAssignedRoutineDialog";
import SaveProgramDialog from "@/components/coach/routines/SaveProgramDialog";
import BlockFooter from "@/components/coach/calendar/BlockFooter";
import GroupByStudentsSelector from "@/components/coach/calendar/GroupByStudentsSelector";
import MonthSelector from "@/components/coach/calendar/MonthSelector";
import GroupBySelector from "@/components/statistics/GroupBySelector";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import ExerciseEvent from "@/components/calendar/ExerciseEvent";
import NoRoutinesInDate from "@/components/calendar/NoRoutinesInDate";
import DayLabelCalendar from "@/components/calendar/DayLabelCalendar";
import LockDaysDialog from "@/components/calendar/LockDaysDialog";
import NotesRoutineDialog from "@/components/coach/NotesRoutineDialog";

export default {
    name: "CalendarDesktop",
    components: {
        NotesRoutineDialog,
        LockDaysDialog,
        DayLabelCalendar,
        NoRoutinesInDate,
        ExerciseEvent,
        ButtonTooltip,
        LoadingSpinner,
        GroupBySelector,
        MonthSelector,
        GroupByStudentsSelector,
        BlockFooter,
        SaveProgramDialog,
        SaveAssignedRoutineDialog,
        SelectReplaceAssignedRoutinesDialog,
        KimsaConfirm,
        CopyRoutineDialog,
        KimsaCombobox,
        AddExerciseDesktopDialog,
        draggable
    },
    data: (vm) => ({
        // Calendar
        firstTime: true,
        focus: '',
        dateTypesOptions: [
            {name: vm.$t('common.day'), value: 'day'},
            {name: vm.$t('common.week'), value: 'week'},
            {name: vm.$t('common.month'), value: 'month'}
        ],
        dateType: 'month',
        weekdays: [1, 2, 3, 4, 5, 6, 0],
        currentMonth: null,
        currentYear: null,
        days: [
            vm.$t('days.sunday'),
            vm.$t('days.monday'),
            vm.$t('days.tuesday'),
            vm.$t('days.wednesday'),
            vm.$t('days.thursday'),
            vm.$t('days.friday'),
            vm.$t('days.saturday')
        ],

        // Delete confirm
        loadingDelete: false,
        confirmTitle: '',
        confirmContent: '',
        confirmDeleteExercise: false,

        // Events
        loadingEvents: false,
        highestBlock: 0,
        blockColorsBase: ['#66CCCC', '#078571', '#99CC00'],
        blockColors: [],
        events: [],
        currentEvent: null,

        // Coaches/Students
        coaches: [],
        students: [],
        currentCoach: null,
        currentStudent: null,
        currentGroupBy: 'students',
        currentGroup: null,
        currentTeam: null,
        currentTeamGroup: null,

        // Copy/Replace Routines
        assignedRoutinesInSameDate: [],
        assignedRoutineToReplace: [],
        currentDate: null,
        loadingCopyWeek: false,

        // Week/Month progress
        weekStatus: [
            {name: vm.$t('common.finished'), value: 'finished', color: '#4cb351'},
            {name: vm.$t('common.incomplete'), value: 'incomplete', color: '#ac7d1d'},
            {name: vm.$t('common.to_be_done'), value: 'to_be_done', color: '#9a2d2d'},
        ],
        currentWeekStatus: null,
        weekOptions: [
            {name: vm.$t('common.week'), value: 'week'},
            {name: vm.$t('common.month'), value: 'month'},
        ],
        currentWeekOption: 'week',

        // LockDays
        lockDays: [],

        // Dragging
        dragging: false,
        draggingEvent: null,
        mouseX: 0,
        mouseY: 0,
        loadingDragging: false,
    }),
    computed: {
        ...mapState(['routinesStudent', 'groupRoutines', 'teamGroupRoutines', 'routineNotes', 'groups', 'teams']),
        dates(vm = this) {
            const uniqueDates = new Set(vm.events.map(event => event.date));
            const dates = [];

            uniqueDates.forEach(fecha => {
                const event = vm.events.find(event => event.date === fecha);
                dates.push({
                    date: vm.$moment(event.date).format('DD-MM-YYYY'),
                    routineId: event.routineId,
                    assignedRoutineId: event.assignedRoutineId,
                });
            })

            return dates.sort((a, b) => Number(a.date.substr(0, 2)) - Number(b.date.substr(0, 2)))
        },
        teamGroups(vm = this) {
            return vm.currentTeam?.groups.map(group => ({
                teamId: vm.currentTeam.id,
                ...group,
                id: group.id,
                leftIcon: 'mdi-account-multiple',
                rightIcon: vm.getIconWeek(group.weekStatus || 'to_be_done')
            })) || []
        },
        isSelectedOption(vm = this) {
            return vm.currentStudent || vm.currentGroup || (vm.currentTeamGroup && vm.currentTeamGroup)
        },
        alertMessage(vm = this) {
            if (vm.currentGroupBy === 'students') return vm.$t('exercises.need_select_an_student')
            if (vm.currentGroupBy === 'groups') return vm.$t('exercises.need_select_an_group')
            if (vm.currentGroupBy === 'teams') return vm.$t('exercises.need_select_an_team')
            return ''
        },
        currentRoutines(vm = this) {
            if (vm.currentGroupBy === 'students') return vm.routinesStudent
            if (vm.currentGroupBy === 'groups') return vm.groupRoutines
            if (vm.currentGroupBy === 'teams') return vm.teamGroupRoutines
            return []
        },
        currentSelection(vm = this) {
            if (vm.currentGroupBy === 'students') return vm.currentStudent
            if (vm.currentGroupBy === 'groups') return vm.currentGroup
            if (vm.currentGroupBy === 'teams') return vm.currentTeamGroup
            return null
        },
        collectionName(vm = this) {
            return vm.currentGroupBy === 'students' ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        groupsWithIcons(vm = this) {
            return vm.groups.map(group => ({
                ...group,
                id: group.id,
                leftIcon: 'mdi-account-multiple',
                rightIcon: vm.getIconWeek(group.weekStatus || 'to_be_done')
            }))
        },
        isCompany(vm = this) {
            return vm.accountType === 'company'
        },
        coachId(vm = this) {
            return vm.isCompany ? vm.currentCoach?.objectID : vm.currentUser?.uid || null
        }
    },
    async mounted() {
        const vm = this
        if (vm.isCompany) {
            await vm.loadCoaches()
        } else {
            await vm.loadStudents()
            if (vm.groups.length === 0) await vm.bindGroups(vm.coachId)
            if (vm.teams.length === 0) await vm.bindTeams(vm.coachId)
        }
        if (vm.$refs.calendar) vm.$refs.calendar.checkChange()
    },
    methods: {
        ...mapActions(['fetchRoutinesStudent', 'fetchGroupRoutines', 'fetchTeamGroupRoutines', 'fetchRoutineNotes', 'bindGroups', 'bindTeams']),

        // Loaders
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.currentUser.uid} AND coachSubscription.paused != 1`
            await Client.clearCache()
            let response = await UserIndex.search('', {filters: filter, hitsPerPage: 10000})
            vm.coaches = response.hits.map(hit => ({...hit, name: hit.name || hit.email}))
            let coachId = vm.$route.query.coachId
            if (coachId) vm.currentCoach = vm.coaches.find(coach => coach.objectID === coachId)
        },
        async loadStudents() {
            const vm = this
            let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
            await Client.clearCache()
            let response = await UserIndex.search('', {filters: filter, hitsPerPage: 10000})
            vm.students = response.hits.map(hit => {
                let leftIcon = (!!hit.team && hit.team) || (!!hit.group && hit.group) ? 'mdi-account-multiple' : 'mdi-account'
                if (hit.hasInjury) leftIcon = 'mdi-account-injury-outline'

                return {
                    ...hit,
                    leftIcon: leftIcon,
                    rightIcon: vm.getIconWeek(hit.weekStatus || 'to_be_done')
                }
            })
            let studentId = vm.$route.query.studentId
            if (studentId) vm.currentStudent = vm.students.find(student => student.objectID === studentId)
        },
        async loadRoutines(date) {
            const vm = this
            let startDate = vm.$moment(date, 'YYYY-M-DD').format('YYYY-MM-DD')
            if (vm.currentGroupBy === 'students')
                await vm.fetchRoutinesStudent({
                    studentId: vm.currentStudent.objectID,
                    date: startDate,
                })
            if (vm.currentGroupBy === 'groups')
                await vm.fetchGroupRoutines({
                    groupId: vm.currentGroup.id,
                    date: startDate,
                })
            if (vm.currentGroupBy === 'teams')
                await vm.fetchTeamGroupRoutines({
                    teamId: vm.currentTeam.id,
                    groupId: vm.currentTeamGroup.id,
                    date: startDate,
                })
        },
        async loadLockDays(date) {
            const vm = this
            let startDate = vm.$moment(date, 'YYYY-M-DD').format('YYYY-MM-DD')
            let endDate = vm.$moment(date, 'YYYY-M-DD').add(1, 'month').format('YYYY-MM-DD')

            let lockDays = await db.collection('lockDays')
                .where('studentId', '==', vm.currentStudent.objectID)
                .where('date', ">=", startDate)
                .where('date', "<", endDate)
                .orderBy('date', 'asc').get()

            vm.lockDays = lockDays.docs.map(day => {
                return {id: day.id, ...day.data()}
            })
            // MOSTRAR ESTOS LOCKDAYS EN EL CALENDARIO
        },
        async loadRoutineNotes(date) {
            const vm = this
            let startDate = vm.$moment(date, 'YYYY-M-DD').format('YYYY-MM-DD')
            await vm.fetchRoutineNotes({
                studentId: vm.currentStudent?.objectID || null,
                teamId: vm.currentTeam?.id || null,
                groupId: vm.getGroupId(),
                date: startDate,
            })
        },
        getGroupId() {
            const vm = this
            if (vm.currentGroup?.id) return vm.currentGroup.id
            if (vm.currentTeamGroup?.id || vm.currentTeamGroup?.id === 0) return vm.currentTeamGroup.id
            return null
        },

        // Calendar
        viewDay(date) {
            const vm = this
            vm.focus = date
            vm.dateType = 'day'
        },
        thisMonth(month) {
            const vm = this
            return vm.currentMonth === month
        },
        getDayString(date) {
            const vm = this
            return vm.days[date.weekday]
        },
        hasEventsToday(date) {
            return this.events.some(event => event.date === date)
        },
        getAbsoluteMonths(month, year) {
            return month + (year * 12);
        },
        triggerUpdateCalendar(currentSelection) {
            if (!currentSelection) return
            const vm = this
            vm.highestBlock = 0
            const start = {
                date: `${vm.currentYear}-${vm.currentMonth}-01`,
                month: vm.currentMonth,
                year: vm.currentYear,
            }

            if (vm.firstTime) {
                vm.updateRange({start})
            } else {
                let endMonths = vm.getAbsoluteMonths(vm.$moment().month() + 1, vm.$moment().year())
                let monthDifference = endMonths - vm.getAbsoluteMonths(vm.currentMonth, vm.currentYear)
                monthDifference === 0 ? vm.updateRange({start}) : vm.$refs.calendar.move(monthDifference)
            }
            if (vm.loadingEvents) vm.firstTime = false
        },
        async updateRange({start}) {
            const vm = this
            vm.currentMonth = start.month
            vm.currentYear = start.year
            if (!vm.isSelectedOption) return
            vm.loadingEvents = true
            vm.events = []
            // console.log('start updateRANGE', start)

            await vm.loadRoutines(start.date)
            if (vm.currentStudent) await vm.loadLockDays(start.date)
            await vm.loadRoutineNotes(start.date)

            // console.log('currentRoutines', vm.currentRoutines)

            for (const routine of vm.currentRoutines) {
                let blocks = await getMergedBlocks(routine.blocks || [])
                vm.pushToEvents(clearBlocksAssignedRoutine(blocks, false), routine.rawDate.full, routine)
            }
            vm.loadingEvents = false
        },
        pushToEvents(blocks, date, routine) {
            const vm = this
            blocks.forEach((block, i) => {
                const blockColor = vm.getColorBlock(i)
                block.exercises.forEach((exercise, j) => {
                    if (exercise.type === 'exercise') {
                        const event = vm.generateEvent(exercise, date, i, blockColor, routine, j)
                        vm.events.push(event)
                    }
                })
            })
        },
        generateEvent(exercise, date, blockIndex, blockColor, routine, exerciseIndex) {
            return {
                ...exercise,
                name: exercise.name || this.$tc('exercises.exercises', 1),
                start: date,
                block: blockIndex + 1,
                color: blockColor,
                date: date,
                routineId: routine.routineId || null,
                assignedRoutineId: routine.id,
                exerciseIndex: exerciseIndex,
                uid: exercise.uid || randomString(10, '', 'ex'),
            }
        },

        // Update calendar
        formatPayloadAddExercise(event) {
            return {
                assignedRoutineId: event.assignedRoutineId,
                routineId: event.routineId || null,
                date: event.date
            }
        },
        async updateBlockExercises(event, addEvent = false) {
            if (!event) return []
            if (event.event) event = event.event

            const vm = this
            let currentBlock = event.block - 1
            let exerciseIndex = event.exerciseIndex

            let arBlocks = clearBlocksAssignedRoutine(vm.currentRoutines.find(routine => routine.id === event.assignedRoutineId).blocks, false)

            if (addEvent) {
                if (vm.draggingEvent.assignedRoutineId === event.assignedRoutineId) {
                    arBlocks[vm.draggingEvent.block - 1].exercises.splice(vm.draggingEvent.exerciseIndex, 1)
                }
                arBlocks[currentBlock].exercises.splice(exerciseIndex + 1, 0, getPayloadExercise(vm.draggingEvent))
            } else {
                arBlocks[currentBlock].exercises.splice(exerciseIndex, 1)
            }

            arBlocks = arBlocks.filter(block => block.exercises.length > 0)

            console.log('arBlocks', arBlocks)
            if (arBlocks.length === 0) {
                console.log('debo borrar rutina, sin mas bloques...')

                if (vm.collectionName === 'groupAssignedRoutines') {
                    let routineRef = await db.collection(vm.collectionName).doc(vm.draggingEvent.assignedRoutineId).get()
                    await deleteAssignedRoutinesGroup(routineRef.data().assignedRoutinesIds)
                }

                await db.collection(vm.collectionName).doc(event.assignedRoutineId).delete()
            } else {
                console.log('solo edito la rutina porque quedan ' + arBlocks.length + ' bloques en la rutina')

                if (vm.collectionName === 'groupAssignedRoutines') {
                    let routineRef = await db.collection(vm.collectionName).doc(event.assignedRoutineId).get()
                    await updateAssignedRoutinesGroup(routineRef.data().assignedRoutinesIds, arBlocks)
                }

                await db.collection(vm.collectionName).doc(event.assignedRoutineId).update({
                    blocks: arBlocks
                })
            }
            return arBlocks
        },

        // Events Style
        getColorBlock(index) {
            const vm = this
            while (vm.blockColors.length <= index) {
                vm.blockColors.push(vm.blockColorsBase[vm.blockColors.length % vm.blockColorsBase.length]);
            }
            return vm.blockColors[index];
        },
        getEventStyles(event) {
            const vm = this
            if (vm.dragging && event.event.uid === vm.draggingEvent?.uid) {
                return {
                    position: 'fixed',
                    left: `${vm.mouseX}px`,
                    top: `${vm.mouseY}px`,
                    cursor: 'move !important',
                    zIndex: '9999 !important',
                    opacity: 0.6,
                };
            } else return {}
        },

        // Dragging
        startDrag(event, mouse) {
            const vm = this
            if (mouse.target.tagName !== 'DIV') {
                vm.draggingEvent = null
                vm.dragging = false
                return;
            }
            vm.mouseX = mouse.x + 10
            vm.mouseY = mouse.y - 10
            vm.draggingEvent = event.event
            vm.dragging = true
        },
        async endDrag(event) {
            const vm = this
            if (!vm.draggingEvent) return;
            if (vm.loadingDragging) return;
            if (vm.draggingEvent.exerciseIndex === event.event.exerciseIndex &&
                vm.draggingEvent.assignedRoutineId === event.event.assignedRoutineId &&
                vm.draggingEvent.block === event.event.block) return vm.draggingEvent = null;

            vm.loadingDragging = true

            if (vm.draggingEvent.assignedRoutineId !== event.event.assignedRoutineId) {
                await vm.updateBlockExercises(vm.draggingEvent)
                await vm.onAddExercise(vm.formatPayloadAddExercise(vm.draggingEvent))
            }

            await vm.updateBlockExercises(event, true)

            await vm.onAddExercise(vm.formatPayloadAddExercise(event.event))

            vm.loadingDragging = false
            vm.dragging = false
            vm.draggingEvent = null
        },
        async endDragFreeSpace(event) {
            const vm = this
            vm.dragging = false
            if (vm.hasEventsToday(event.date) || !vm.draggingEvent || !vm.thisMonth(event.month)) return;

            vm.loadingDragging = true

            let blocks = [{
                completed: false,
                duration: 0,
                unit: vm.$tc('common.minutes', 2),
                id: 1,
                exercises: [getPayloadExercise(vm.draggingEvent)]
            }]

            let payloadAssignedRoutine = getPayloadBaseAssignedRoutine(`${vm.currentSelection.name} - ${vm.$moment(event.date).format('DD-MM-YYYY')}`, event.date, {coachId: vm.coachId}, blocks)

            if (vm.collectionName === 'assignedRoutines') {
                payloadAssignedRoutine.studentId = vm.currentSelection.objectID
            } else {
                payloadAssignedRoutine.teamId = vm.currentSelection.teamId || null
                payloadAssignedRoutine.groupId = vm.currentSelection.id
            }

            let newAssignedRoutine = await db.collection(vm.collectionName).add(payloadAssignedRoutine)

            // update old assigned routine
            await vm.updateBlockExercises(vm.draggingEvent)
            vm.events = vm.events.filter(ev => ev.date !== vm.draggingEvent.date)
            await vm.onAddExercise({assignedRoutineId: newAssignedRoutine.id, date: event.date})

            // console.log('payloadAssignedRoutine',payloadAssignedRoutine)

            if (vm.collectionName === 'groupAssignedRoutines') {
                let students = [...vm.currentSelection.students]
                if (vm.assignedRoutineToReplace.length > 0) {
                    let toDelete = vm.assignedRoutineToReplace.filter(ar => ar.replace).map(ar => {
                        return ar.assignedRoutineId
                    })
                    if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)
                    let skippedStudents = vm.assignedRoutineToReplace.filter(ar => !ar.replace).map(ar => {
                        return ar.studentId
                    })
                    if (skippedStudents.length > 0) students = students.filter(st => !skippedStudents.includes(st))
                }
                await addAssignedRoutinesGroup({id: newAssignedRoutine.id, ...payloadAssignedRoutine, date: event.date}, students)
            }

            vm.loadingDragging = false
            vm.draggingEvent = null
        },
        mouseMoveUpdate(event, mouse) {
            const vm = this
            if (vm.dragging) {
                vm.mouseX = mouse.x + 10
                vm.mouseY = mouse.y - 10
            }
        },

        // Set current selection
        setCurrentCoach(coach) {
            const vm = this;
            vm.currentCoach = coach?.name ? coach : vm.coaches?.find(co => co.name.toLowerCase() === coach?.toLowerCase()) || null
        },
        setCurrentStudent(student) {
            const vm = this
            vm.currentStudent = student?.name ? student : vm.students?.find(st => st.name.toLowerCase() === student?.toLowerCase()) || null
            vm.currentWeekOption = vm.currentStudent?.weekStatusOption || 'week'
            vm.currentWeekStatus = vm.currentStudent?.weekStatus || 'to_be_done'
        },
        setCurrentGroup(group) {
            const vm = this
            vm.currentGroup = group?.name ? group : vm.groups?.find(gr => gr.name.toLowerCase() === group?.toLowerCase()) || null
            vm.currentWeekOption = vm.currentGroup?.weekStatusOption || 'week'
            vm.currentWeekStatus = vm.currentGroup?.weekStatus || 'to_be_done'
        },
        setCurrentTeam(team) {
            const vm = this
            vm.currentTeam = team?.name ? team : vm.teams?.find(tm => tm.name.toLowerCase() === team?.toLowerCase()) || null
        },
        setCurrentTeamGroup(teamGroup) {
            const vm = this
            vm.currentTeamGroup = teamGroup?.name ? teamGroup : vm.teamGroups?.find(tmg => tmg.name.toLowerCase() === teamGroup?.toLowerCase()) || null
            vm.currentWeekOption = vm.currentTeamGroup?.weekStatusOption || 'week'
            vm.currentWeekStatus = vm.currentTeamGroup?.weekStatus || 'to_be_done'
        },
        resetTeam() {
            const vm = this
            vm.currentTeam = null
            vm.currentTeamGroup = null
        },

        // Copy routines
        showCopyRoutine(date) {
            const vm = this
            vm.$refs.copy.show(date)
        },
        async onCopyRoutine(assignedRoutines) {
            const vm = this
            // console.log('payload copied', assignedRoutines)
            if (assignedRoutines.length > 0) {
                for (const routine of assignedRoutines) {
                    let blocks = await getMergedBlocks(routine.blocks)
                    vm.pushToEvents(blocks, routine.rawDate.full, routine)
                    await vm.loadRoutines(vm.$moment(routine.rawDate.full).format('YYYY-M-DD'))
                }
            }
            vm.toast(vm.$t('routines.copied'))
        },
        async loadAssignedRoutinesInSameDate(date, isDraggingEvent = false) {
            const vm = this
            let assignedRoutinesInSameDate = []
            let students = vm.currentGroup?.students || null
            if (!students) students = vm.currentTeamGroup.students

            for (let id of students) {
                let assignedRoutineInDate = await hasAssignedRoutineInDate(id, date)
                if (assignedRoutineInDate.has) {
                    let studentName = await db.collection('users').doc(id).get()
                    assignedRoutinesInSameDate.push({
                        studentId: id,
                        studentName: studentName.data().name,
                        assignedRoutineId: assignedRoutineInDate.id,
                        replace: false,
                    })
                }
            }

            if (assignedRoutinesInSameDate.length > 0) {
                vm.assignedRoutinesInSameDate = assignedRoutinesInSameDate
                vm.$refs.replaceRoutines.show(date)
            } else if (!isDraggingEvent) {
                vm.$refs.addExerciseDialog.show(date, null)
            }
        },
        onConfirmReplace({date, toReplace}) {
            const vm = this
            vm.assignedRoutineToReplace = toReplace
            vm.$refs.addExerciseDialog.show(date, null)
        },
        // Copy week
        showCopyWeek(date) {
            const vm = this
            vm.currentDate = date
            vm.$refs.copyWeek.show()
        },
        async onConfirmCopyWeek() {
            const vm = this
            vm.loadingCopyWeek = true
            let startDate = vm.$moment(vm.currentDate).startOf('week').add(-1, 'day')
            let events = []
            for (let i = 0; i < 7; i++) {
                let currentDate = startDate.add(1, 'day').format('YYYY-MM-DD')
                let foundRoutine = vm.currentRoutines.find(routine => routine.rawDate.full === currentDate)
                if (foundRoutine) events.push(foundRoutine)
            }

            let collection = vm.currentGroupBy === 'students' ? 'assignedRoutines' : 'groupAssignedRoutines'
            let assignedRoutines = []

            for (let routine of events) {
                const date = vm.$moment(routine.rawDate.full).add(7, 'day')
                const dateFormatted = date.format('YYYY-MM-DD')
                const routineName = `${routine.name.split(' - ')[0]} - ${date.format('DD-MM-YYYY')}`
                let blocks = clearBlocksAssignedRoutine(routine.blocks)
                let payload = getPayloadBaseAssignedRoutine(routineName, dateFormatted, routine, blocks)

                if (vm.currentGroupBy === 'students') {
                    payload.studentId = routine.studentId
                } else {
                    payload.groupId = routine.id
                    payload.teamId = routine.teamId || null
                }

                let currentStudents = payload.studentId ? [payload.studentId] : vm.currentSelection?.students || []
                vm.events = vm.events.filter(event => event.date !== dateFormatted)

                for (let studentId of currentStudents) await deleteAssignedRoutineStudentInDate(studentId, dateFormatted)

                let assignedRoutineCreated = await db.collection(collection).add(payload)

                if (collection === 'groupAssignedRoutines')
                    await addAssignedRoutinesGroup({id: assignedRoutineCreated.id, ...payload, date: dateFormatted}, currentStudents)

                assignedRoutines.push({
                    ...payload,
                    date: dateFormatted,
                    id: assignedRoutineCreated.id
                })
            }

            await vm.onCopyRoutine(assignedRoutines)

            vm.loadingCopyWeek = false
            vm.currentDate = null
            vm.$refs.copyWeek.hide()
        },

        // Delete routines
        showDeleteRoutine(date) {
            const vm = this
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let findDate = vm.dates.find(d => d.date === dateFormatted)
            let assignedRoutineId = findDate?.assignedRoutineId || null

            if (!assignedRoutineId) return vm.toast(vm.$t('routines.error_with_routine'), 'error')

            vm.confirmDeleteExercise = false
            vm.confirmTitle = vm.$t('routines.delete')
            vm.confirmContent = vm.$t('routines.confirm_delete')
            vm.currentEvent = {assignedRoutineId: assignedRoutineId, date: date}
            vm.$refs.confirm.show()
        },
        async onDeleteRoutine() {
            const vm = this
            vm.loadingDelete = true

            if (vm.collectionName === 'groupAssignedRoutines') {
                let groupRoutineRef = await db.collection(vm.collectionName).doc(vm.currentEvent.assignedRoutineId).get()
                await deleteAssignedRoutinesGroup(groupRoutineRef.data().assignedRoutinesIds)
            }

            await db.collection(vm.collectionName).doc(vm.currentEvent.assignedRoutineId).delete()
            await vm.loadRoutines(vm.$moment(vm.currentEvent.date).format('YYYY-M-DD'))
            vm.events = vm.events.filter(event => event.date !== vm.currentEvent.date)
            vm.loadingDelete = false
            vm.$refs.confirm.hide()
            vm.currentEvent = null
            vm.toast(vm.$t('routines.deleted'))
        },
        // Delete exercise
        showDeleteEvent({event}) {
            const vm = this
            vm.currentEvent = {
                ...event,
                hasMoreExercisesInBlock: vm.events.filter(evt => evt.date === event.date && evt.block === event.block).length > 1
            }
            vm.confirmDeleteExercise = true
            vm.confirmTitle = vm.$t('exercises.delete_exercise')
            vm.confirmContent = vm.$t('exercises.confirm_delete_exercise', {exercise: vm.currentEvent && vm.currentEvent.name || ''})
            vm.$refs.confirm.show()
        },
        async onDeleteExercise() {
            const vm = this
            vm.loadingDelete = true

            let arBlocks = await vm.updateBlockExercises(vm.currentEvent)

            let deleteMessage = arBlocks.length === 0 ? vm.$t('routines.deleted') : vm.$t('exercises.deleted')

            // CLEAN EVENTS TODAY AND PUSH AGAIN
            vm.events = vm.events.filter(event => event.date !== vm.currentEvent.date)
            let blocks = await getMergedBlocks(arBlocks)
            vm.pushToEvents(blocks, vm.currentEvent.date, vm.currentEvent)
            await vm.loadRoutines(vm.currentEvent.date)

            vm.loadingDelete = false
            vm.$refs.confirm.hide()
            vm.currentEvent = null
            vm.toast(deleteMessage)
        },

        // Save routines/programs
        showSaveRoutineDialog(date) {
            const vm = this
            let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
            let findDate = vm.dates.find(d => d.date === dateFormatted)
            let assignedRoutine = {...vm.currentRoutines.find(routine => routine.id === findDate?.assignedRoutineId || null)}
            vm.$refs.saveRoutine.show(assignedRoutine)
        },
        showSaveProgramDialog(date) {
            const vm = this
            vm.$refs.saveProgram.show(date)
        },

        // Add exercise
        async showAddExercise(date) {
            const vm = this
            let events = vm.events.filter(event => event.date === date)
            let lastEventToday = events.length > 0 && events[events.length - 1] || null
            let groupHasStudents = vm.currentGroup?.students?.length > 0 || vm.currentTeamGroup?.students?.length > 0
            !lastEventToday && groupHasStudents ?
                await vm.loadAssignedRoutinesInSameDate(date) :
                vm.$refs.addExerciseDialog.show(date, lastEventToday)
        },
        showEvent({event}) {
            const vm = this
            console.log('show event', event)
            event.hasMoreExercisesInBlock = vm.events.filter(e => e.date === event.date && e.block === event.block).length > 1
            let lastBlock = vm.events.filter(e => e.date === event.date).pop()
            event.lastBlock = lastBlock.block
            vm.$refs.addExerciseDialog.setEditingMode(event)
        },
        async onAddExercise(payload) {
            const vm = this
            let arBlocksRef = await db.collection(vm.collectionName).doc(payload.assignedRoutineId).get()
            vm.events = vm.events.filter(event => event.date !== payload.date)
            let blocks = await getMergedBlocks(arBlocksRef.data().blocks)
            let routine = {id: payload.assignedRoutineId, routineId: payload.routineId || null}
            vm.pushToEvents(blocks, payload.date, routine)
            await vm.loadRoutines(payload.date)
        },

        //Lock days
        showLockDay(dates) {
            const vm = this
            vm.$refs.lockDaysDialog.show(dates)
        },

        // Notes
        noteToday(date) {
            const vm = this
            return vm.routineNotes?.find(note => note.rawDate.full === date) || null
        },
        showNoteDialog(date) {
            const vm = this
            vm.$refs.notes.show(vm.routineNotes?.find(note => note.rawDate.full === date) || null, date)
        },
        async onSaveNotes() {
            const vm = this
            await vm.loadRoutineNotes(vm.$moment().format('YYYY-MM-DD'))
        },

        // Week options/status
        getIconWeek(status) {
            if(status === 'finished')return {icon: 'mdi-check', color: '#4cb351'}
            if(status === 'incomplete') return {icon: 'mdi-pencil', color: '#ac7d1d'}
            if(status === 'to_be_done') return {icon: 'mdi-close', color: '#9a2d2d'}
            return {icon: '', color: '#000000'}
        },
        async onChangeWeekOption(weekOption) {
            const vm = this
            if (vm.currentSelection?.weekStatus !== weekOption) {
                if (vm.currentGroupBy === 'students')
                    await vm.updateUserWeekStatus(vm.currentSelection.objectID, 'weekStatusOption', weekOption)
                if (vm.currentGroupBy === 'groups') {
                    await db.collection('groups').doc(vm.currentSelection.id).update({weekStatusOption: weekOption})
                    for (let studentId of vm.currentSelection?.students || [])
                        await vm.updateUserWeekStatus(studentId, 'weekStatusOption', weekOption)
                }
                if (vm.currentGroupBy === 'teams') {
                    let groups = [...vm.currentTeam?.groups || []]
                    let findGroupIndex = groups.findIndex(group => group.id === vm.currentSelection.id)
                    if (findGroupIndex >= 0) groups[findGroupIndex].weekStatusOption = weekOption
                    await db.collection('teams').doc(vm.currentSelection.teamId).update({groups: groups})

                    if (findGroupIndex >= 0)
                        for (let studentId of groups[findGroupIndex]?.students || [])
                            await vm.updateUserWeekStatus(studentId, 'weekStatusOption', weekOption)
                }
            }
        },
        async updateUserWeekStatus(studentId, attribute, value) {
            const vm = this
            await db.collection('users').doc(studentId).update({[attribute]: value})
            if (attribute === 'weekStatus') {
                const findUserIndex = vm.students.findIndex(student => student.objectID === studentId)
                if (findUserIndex >= 0) vm.students[findUserIndex].rightIcon = vm.getIconWeek(value)
            }
        }
    },
    watch: {
        async currentCoach() {
            const vm = this
            vm.currentStudent = null
            await vm.loadStudents()
            await vm.bindGroups(vm.coachId)
            await vm.bindTeams(vm.coachId)
        },
        currentStudent(val) {
            this.triggerUpdateCalendar(val)
        },
        currentGroup(val) {
            this.triggerUpdateCalendar(val)
        },
        currentTeam(val) {
            this.triggerUpdateCalendar(val)
        },
        currentTeamGroup(val) {
            this.triggerUpdateCalendar(val)
        },
        currentGroupBy(val) {
            if (val === 'students') {
                this.currentGroup = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'groups') {
                this.currentStudent = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'teams') {
                this.currentStudent = null
                this.currentGroup = null
            }
        },
        async currentWeekStatus(val, oldVal) {
            if (!oldVal && !this.currentSelection) return
            const vm = this
            if (vm.currentGroupBy === 'students')
                await vm.updateUserWeekStatus(vm.currentSelection.objectID, 'weekStatus', val)
            if (vm.currentGroupBy === 'groups') {
                await db.collection('groups').doc(vm.currentSelection.id).update({weekStatus: val})
                for (let studentId of vm.currentSelection?.students || [])
                    await vm.updateUserWeekStatus(studentId, 'weekStatus', val)
            }
            if (vm.currentGroupBy === 'teams') {
                let groups = [...vm.currentTeam?.groups || []]
                let findGroupIndex = groups.findIndex(group => group.id === vm.currentSelection.id)
                if (findGroupIndex >= 0) groups[findGroupIndex].weekStatus = val
                await db.collection('teams').doc(vm.currentSelection.teamId).update({groups: groups})

                if (findGroupIndex >= 0)
                    for (let studentId of groups[findGroupIndex]?.students || [])
                        await vm.updateUserWeekStatus(studentId, 'weekStatus', val)
            }
        },
        highestBlock(val) {
            const vm = this
            if (val > vm.blockColors.length) {
                for (let i = vm.blockColors.length; i < val; i++) vm.blockColors.push(vm.blockColorsBase[i % vm.blockColorsBase.length])
            } else if (val < vm.blockColors.length) vm.blockColors = vm.blockColors.slice(0, val)
        },
        currentRoutines(val) {
            let highestBlock = 0
            val.forEach(routine => {
                if(routine.blocks.length > highestBlock) highestBlock = routine.blocks.length
            })
            this.highestBlock = highestBlock
        }
    }
}
</script>

<style scoped>

>>> .v-calendar-daily__scroll-area {
    display: none;
}

>>> .v-calendar-daily__intervals-head {
    display: none;
}

>>> .theme--light.v-calendar-weekly .v-calendar-weekly__head-weekday {
    color: #b1cd44 !important;
    text-transform: lowercase;
}

.calendar {
    box-shadow: 0 4px 8px rgba(217, 217, 217, 0.19) !important;
    border-radius: 8px !important;
    margin-bottom: 16px !important;
    overflow-x: hidden;
    background: transparent !important;
    border: 1px solid #D9D9D9 !important;
}

/* Change Days labels */
>>> .v-calendar-weekly__head-weekday {
    text-transform: none !important;
    font-weight: 400;
    font-size: 13px;
    line-height: 13px;
    padding-top: 8px;
    padding-bottom: 2px;
}

::v-deep .v-calendar .v-event {
    background-color: transparent !important;
}

.tip-dragging {
    position: fixed;
    top: 8px;
    right: 8px;
    padding: 8px;
    z-index: 999;
    max-width: 360px;
}

.calendar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

</style>
